<template>
    <div class="w-100 user-modal-password-reset-view px-2">
        <div class="mb-5 text-center">
            <p class="font-weight-bold font-size-200">{{ translate('Reset Password') }}</p>
            <p class="mb-0">{{ translate('Reset the password you use to log into your account.') }}</p>
        </div>

        <div class="mb-4" @keyup.enter="requestPasswordReset">
            <TextInput
                data-user-modal-input-email
                type="email"
                name="u_email"
                :label="translate('E-Mail')"
                icon="far fa-envelope"
                @input="updateEmail"
                :disabled="isSubmitting"
                :has-error="emailError !== null"
                :current-error="emailError"
                :trim-input="true"
                autocomplete="email"
            ></TextInput>
        </div>

        <ButtonWithLoadingState
            data-user-modal-button-submit
            class="btn btn-success btn-lg btn-block font-weight-bold text-uppercase rounded mb-4"
            icon="fa fa-unlock-alt"
            :text="translate('Reset Password')"
            :is-state-loading="isSubmitting"
            @click.stop.prevent="requestPasswordReset"
        ></ButtonWithLoadingState>

        <div class="text-center">
            {{ translate('Having trouble?') }}
            <a data-user-modal-link-help href="" @click.stop.prevent="goToHelpPage">{{ translate('Get help') }}</a>
        </div>

        <div class="text-center mt-2">
            {{ translate('Already have an account?') }}
            <RouterLink
                data-user-modal-link-login
                :to="{ name: userModalRoute.LOGIN }"
                :disabled="isSubmitting"
                :event="isSubmitting ? '' : 'click'"
                tag="a"
                >{{ translate('Log in') }}
            </RouterLink>
        </div>

        <hr class="mb-4 mt-4" />

        <div class="text-center">
            {{ translate("Don't have an account yet?") }}
            <RouterLink
                data-user-modal-link-register
                :to="{ name: userModalRoute.REGISTER }"
                :disabled="isSubmitting"
                :event="isSubmitting ? '' : 'click'"
                tag="a"
                >{{ translate('Sign up') }}
            </RouterLink>
        </div>
    </div>
</template>

<script>
import TextInput from '../../../../../qaamgo/modules/sat/components/form/TextInput';
import TransFilter from '../../../../../qaamgo/modules/sat/components/mixins/TransFilter';
import { translate } from '../../../../../qaamgo/helper/translate';
import { testIsNotNull, testIsTrimmedStringEmpty } from '../../../../../lib/test-and-assert/test-base';
import { pageStoreMixin } from '../../../../store/page-store';
import { userManager } from '../../../user-manager';
import { pageNotification } from '../../../../../qaamgo/helper/notify';
import { userModalRoute } from '../router';
import ButtonWithLoadingState from '../../../../../qaamgo/modules/sat/components/form/ButtonWithLoadingState';
import { testIsEmailValid } from '../../../test';
import {
    PASSWORD_RESET_SEND_MAIL_FAIL_INVALID_CAPTCHA,
    PASSWORD_RESET_SEND_MAIL_FAIL_INVALID_CSRF,
    PASSWORD_RESET_SEND_MAIL_FAIL_INVALID_EMAIL,
} from '../../../user-api';
import { translatePath } from '../../../../../qaamgo/sat/helper/translate-path';
import { satGlobals } from '../../../../../qaamgo/sat/helper/sat-globals';
import { globalLogger } from '../../../../../qaamgo/helper/global-logger';

export default {
    name: 'PasswordResetView',
    components: { ButtonWithLoadingState, TextInput },
    mixins: [TransFilter, pageStoreMixin],
    computed: {
        /** @return {Boolean} */
        isSubmitting() {
            return this.store.userPaymentModal.isSubmitting;
        },
    },
    methods: {
        goToHelpPage() {
            const link = satGlobals.getMainUrl() + translatePath('/help');

            window.open(link, '_blank');
        },
        updateEmail(value) {
            this.emailValue = value;

            this.emailError = null;
        },
        validateEmailAndSetErrors() {
            if (testIsTrimmedStringEmpty(this.emailValue)) {
                this.emailError = translate('Enter your e-mail address');

                return;
            }

            if (!testIsEmailValid(this.emailValue)) {
                this.emailError = translate('E-Mail is not valid');

                return;
            }

            this.emailError = null;
        },
        showResetSuccess() {
            let emailSentText = translate(
                'If you have an account for <strong>%email%</strong>, you will soon find instructions in your inbox on how to change your password.'
            );

            emailSentText = emailSentText.replace('%email%', this.emailValue);

            this.store.userPaymentModal.setSubmitting(false);
            this.$router.push({
                name: userModalRoute.LOGIN,
                params: {
                    emailSentText: emailSentText,
                },
            });
        },
        async requestPasswordReset() {
            this.validateEmailAndSetErrors();

            if (testIsNotNull(this.emailError)) {
                return;
            }

            this.store.userPaymentModal.setSubmitting(true);
            globalLogger.log('user modal - password reset - start');

            try {
                await userManager.sendPasswordResetMail(this.emailValue);

                globalLogger.log('user modal - password reset - success');

                this.showResetSuccess();
            } catch (error) {
                globalLogger.log('user modal - password reset - fail', error);

                if (error === PASSWORD_RESET_SEND_MAIL_FAIL_INVALID_EMAIL) {
                    this.emailError = translate('E-Mail is not valid');
                    this.store.userPaymentModal.setSubmitting(false);

                    return;
                }

                if (
                    error === PASSWORD_RESET_SEND_MAIL_FAIL_INVALID_CAPTCHA ||
                    error === PASSWORD_RESET_SEND_MAIL_FAIL_INVALID_CSRF
                ) {
                    // only csrf/captcha errors should end up here
                    pageNotification.showGenericErrorMessage();
                    this.store.userPaymentModal.setSubmitting(false);

                    return;
                }

                // user might not exist
                // -> don't act suspicious, pretend everything is fine
                this.showResetSuccess();
            }
        },
    },
    data() {
        return {
            userModalRoute: userModalRoute,
            emailValue: '',
            emailError: null,
        };
    },
};
</script>
